/**
 * Client-side Data Sanitizer
 * Removes or redacts PII and sensitive data before sending to AI service
 */

// Fields that should be completely excluded
const EXCLUDED_FIELDS = new Set([
    'ssn', 'social_security', 'socialsecuritynumber', 'social_security_number',
    'tax_id', 'taxid', 'tin', 'ein',
    'driver_license', 'driverlicense', 'dl_number',
    'passport', 'passport_number', 'passportnumber',
    'credit_card', 'creditcard', 'card_number', 'cardnumber', 'cc_number',
    'bank_account', 'bankaccount', 'account_number', 'accountnumber',
    'routing_number', 'routingnumber',
    'password', 'pwd', 'secret', 'api_key', 'apikey', 'private_key',
    'access_token', 'refresh_token', 'auth_token', 'bearer',
    'cvv', 'cvc', 'pin', 'security_code'
]);

// Fields that should be redacted
const REDACTED_FIELDS = new Set([
    'email', 'e_mail', 'emailaddress', 'email_address',
    'phone', 'telephone', 'mobile', 'cell', 'phone_number', 'phonenumber',
    'address', 'street', 'street_address', 'home_address', 'mailing_address',
    'dob', 'date_of_birth', 'dateofbirth', 'birthdate', 'birth_date',
    'medicare_id', 'medicareid', 'medicaid_id', 'medicaidid',
    'insurance_id', 'insuranceid', 'policy_number', 'policynumber',
    'member_id', 'memberid', 'subscriber_id', 'subscriberid',
    'patient_id', 'patientid', 'mrn', 'medical_record_number',
    'resident_name', 'residentname', 'patient_name', 'patientname',
    'full_name', 'fullname', 'first_name', 'firstname', 'last_name', 'lastname'
]);

// Regex patterns for content-based detection
const PII_PATTERNS = [
    {
        name: 'ssn',
        pattern: /\b\d{3}[-\s]?\d{2}[-\s]?\d{4}\b/g,
        replacement: '[SSN-REDACTED]'
    },
    {
        name: 'credit_card',
        pattern: /\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g,
        replacement: '[CC-REDACTED]'
    },
    {
        name: 'email',
        pattern: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
        replacement: '[EMAIL-REDACTED]'
    },
    {
        name: 'phone',
        pattern: /\b(?:\+?1[-.\s]?)?\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}\b/g,
        replacement: '[PHONE-REDACTED]'
    }
];

/**
 * Sanitize extracted data for AI processing
 * @param {Object} extractedData - Data extracted from Power BI
 * @param {Object} options - Sanitization options
 * @returns {Object} Sanitized data and statistics
 */
export function sanitizeDataForAI(extractedData, options = {}) {
    const {
        enableFieldFilter = true,
        enableContentFilter = true
    } = options;

    const stats = {
        fieldsExcluded: 0,
        fieldsRedacted: 0,
        patternsMatched: 0,
        excludedFieldNames: [],
        redactedFieldNames: []
    };

    const sanitizedData = sanitizeObject(extractedData, stats, {
        enableFieldFilter,
        enableContentFilter
    });

    return {
        data: sanitizedData,
        stats
    };
}

/**
 * Recursively sanitize an object
 */
function sanitizeObject(obj, stats, options, path = '') {
    if (obj === null || obj === undefined) {
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map((item, index) =>
            sanitizeObject(item, stats, options, `${path}[${index}]`)
        );
    }

    if (typeof obj === 'object') {
        const sanitized = {};

        for (const [key, value] of Object.entries(obj)) {
            const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '_');
            const currentPath = path ? `${path}.${key}` : key;

            // Check if field should be excluded
            if (options.enableFieldFilter && EXCLUDED_FIELDS.has(normalizedKey)) {
                stats.fieldsExcluded++;
                stats.excludedFieldNames.push(currentPath);
                continue;
            }

            // Check if field should be redacted
            if (options.enableFieldFilter && REDACTED_FIELDS.has(normalizedKey)) {
                stats.fieldsRedacted++;
                stats.redactedFieldNames.push(currentPath);
                sanitized[key] = '[REDACTED]';
                continue;
            }

            // Recursively process
            if (typeof value === 'object') {
                sanitized[key] = sanitizeObject(value, stats, options, currentPath);
            } else if (typeof value === 'string' && options.enableContentFilter) {
                sanitized[key] = sanitizeString(value, stats);
            } else {
                sanitized[key] = value;
            }
        }

        return sanitized;
    }

    if (typeof obj === 'string' && options.enableContentFilter) {
        return sanitizeString(obj, stats);
    }

    return obj;
}

/**
 * Sanitize string content using pattern matching
 */
function sanitizeString(str, stats) {
    if (!str || typeof str !== 'string') {
        return str;
    }

    let sanitized = str;

    for (const { pattern, replacement } of PII_PATTERNS) {
        const matches = sanitized.match(pattern);
        if (matches) {
            stats.patternsMatched += matches.length;
            sanitized = sanitized.replace(pattern, replacement);
        }
    }

    return sanitized;
}

/**
 * Check if a column name suggests PII
 * @param {string} columnName - Column name to check
 * @returns {Object} Check result
 */
export function checkColumnForPII(columnName) {
    const normalized = columnName.toLowerCase().replace(/[^a-z0-9]/g, '_');

    if (EXCLUDED_FIELDS.has(normalized)) {
        return { isPII: true, type: 'excluded', action: 'remove' };
    }

    if (REDACTED_FIELDS.has(normalized)) {
        return { isPII: true, type: 'redacted', action: 'mask' };
    }

    // Check for partial matches
    const keywords = ['ssn', 'social', 'tax', 'license', 'passport', 'credit',
        'bank', 'password', 'secret', 'key', 'token', 'email', 'phone',
        'address', 'dob', 'birth', 'medicare', 'medicaid', 'insurance',
        'patient', 'resident', 'member', 'name'];

    for (const keyword of keywords) {
        if (normalized.includes(keyword)) {
            return { isPII: true, type: 'suspected', action: 'review', keyword };
        }
    }

    return { isPII: false };
}

/**
 * Get sanitization preview for user confirmation
 * @param {Object} extractedData - Data to analyze
 * @returns {Object} Preview of what will be sanitized
 */
export function getSanitizationPreview(extractedData) {
    const preview = {
        columnsToExclude: [],
        columnsToRedact: [],
        suspectedPII: [],
        totalColumns: 0
    };

    if (extractedData.visuals) {
        extractedData.visuals.forEach(visual => {
            if (visual.columns) {
                visual.columns.forEach(col => {
                    preview.totalColumns++;
                    const check = checkColumnForPII(col);

                    if (check.isPII) {
                        if (check.action === 'remove') {
                            preview.columnsToExclude.push({
                                visual: visual.title || visual.visualId,
                                column: col
                            });
                        } else if (check.action === 'mask') {
                            preview.columnsToRedact.push({
                                visual: visual.title || visual.visualId,
                                column: col
                            });
                        } else {
                            preview.suspectedPII.push({
                                visual: visual.title || visual.visualId,
                                column: col,
                                keyword: check.keyword
                            });
                        }
                    }
                });
            }
        });
    }

    return preview;
}

export default {
    sanitizeDataForAI,
    checkColumnForPII,
    getSanitizationPreview
};
